import React, { useState, useEffect } from 'react';
import { Grid, FormControl, Box, Button } from '@mui/material';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import PropTypes from 'prop-types';
import muiInputStyles from '../MuiInput/MuiInputStyles';
import FilterAutocomplete from '../MuiAutocomplete/FilterAutocomplete';

function TableFilterConfig({ rows, onFilteredDataChange, filterConfig, actions, initialSearchFilter = '', initialFilterBy = '', onFilterCriteriaChange }) {
  const classes = muiInputStyles();

  const [searchFilter, setSearchFilter] = useState(initialSearchFilter);
  const [filterBy, setFilterBy] = useState(initialFilterBy);

  // Re-apply the active filter whenever the source rows are updated (e.g. after a refresh)
  useEffect(() => {
    applyFilter(searchFilter, filterBy);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // Utility to split comma-delimited strings
  const splitAndClean = (input) => {
    if (Array.isArray(input)) {
        return input.map(s => String(s).trim()).filter(Boolean);
    }
    if (typeof input === 'string') {
        return input.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [];
  };

  // Get config object for selected filter type
  const currentConfig = filterConfig.find(f => f.type === searchFilter);

  // Dynamically build options for the selected filter type
  const filterOptions = currentConfig
    ? [...new Set(rows
        .flatMap(row => splitAndClean(row[currentConfig.field]))
        .sort((a, b) => a.localeCompare(b)))]
    : [];

  // Validation: Check if filter type and value are valid
  const isValidFilter = (type, value) => {
    return !!(type && value && filterConfig.find(f => f.type === type));
  };

  // Data transformation: Get selected filter config
  const getFilterConfig = (type) => filterConfig.find(f => f.type === type);

  // Data transformation: Normalize and split values
  const normalizeValues = (input) => splitAndClean(input).map(v => v.toLowerCase());

  // Filtering: Filter rows based on selected values and config
  const filterRows = (rows, config, selectedValues) => {
    return rows.filter(row => {
      const rawField = row[config.field];
      const rowValues = normalizeValues(rawField);
      return selectedValues.some(val => rowValues.includes(val));
    });
  };

  // Main applyFilter function
  const applyFilter = (type, value) => {
    if (!isValidFilter(type, value)) {
      onFilteredDataChange(rows); // Reset
      return;
    }

    const config = getFilterConfig(type);
    const selectedValues = normalizeValues(value);

    const filtered = filterRows(rows, config, selectedValues);

    onFilteredDataChange(filtered);
  };

  const handleFilterTypeChange = (newType) => {
    setSearchFilter(newType);
    setFilterBy('');
    onFilteredDataChange(rows); // Reset
    onFilterCriteriaChange?.(newType, '');
  };

  const handleFilterChange = (value) => {
    setFilterBy(value);
    applyFilter(searchFilter, value);
    onFilterCriteriaChange?.(searchFilter, value);
  };

  const handleClearFilters = () => {
    setSearchFilter('');
    setFilterBy('');
    onFilteredDataChange(rows);
    onFilterCriteriaChange?.('', '');
  };

  return (
    <Grid container spacing={3} alignItems="flex-start">
      <Grid item xs={12} sm={3}>
        <FormControl fullWidth>
          <FilterAutocomplete
            id="searchFilterType"
            className={classes.muiInputs}
            value={currentConfig?.label || null}
            onChange={(e, newLabel) => {
              const selected = filterConfig.find(f => f.label === newLabel);
              handleFilterTypeChange(selected ? selected.type : '');
            }}
            options={filterConfig.map(f => f.label)}
            label="Search Filter"
            placeholder="Select a filter..."
            renderOptionContent={option => option}
          />
        </FormControl>
      </Grid>

      {searchFilter && (
        <Grid item xs={12} sm>
          <FormControl fullWidth>
            <FilterAutocomplete
              multiple
              id="searchFilter"
              className={classes.muiInputs}
              value={splitAndClean(filterBy)}
              onChange={(e, newValue) => handleFilterChange(newValue.join(','))}
              options={filterOptions}
              label={currentConfig?.label}
              placeholder={`${currentConfig?.label} to filter by...`}
              getOptionLabel={option => option}
              renderOptionContent={option => option}
              customFilter={(option, lowerInputValue) =>
                option.toLowerCase().includes(lowerInputValue)
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <span key={index} {...getTagProps({ index })}>
                    {option}
                  </span>
                ))
              }
            />
          </FormControl>
        </Grid>
      )}

      <Grid item xs={12} sm="auto" sx={{ ml: { sm: 'auto' } }}>
        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button
            id="clearFilters"
            aria-label="Clear table filters"
            onClick={handleClearFilters}
            startIcon={<ClearAllIcon className='startIconLarger' />}
            variant="outlined"
            color="primary"
            sx={{ whiteSpace: 'nowrap' }}
          >
            Clear Filters
          </Button>
          {actions}
        </Box>
      </Grid>
    </Grid>
  );
}

TableFilterConfig.propTypes = {
  rows: PropTypes.array.isRequired,
  onFilteredDataChange: PropTypes.func.isRequired,
  filterConfig: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      field: PropTypes.string.isRequired,
    })
  ).isRequired,
  actions: PropTypes.node,
  initialSearchFilter: PropTypes.string,
  initialFilterBy: PropTypes.string,
  onFilterCriteriaChange: PropTypes.func,
};

export default TableFilterConfig;
